import { Link } from 'react-router-dom';
import { Card, CardHeader, CardContent } from '@/components/ui/Card';
import type { StrategyType } from '@steward/shared';
import { Button } from '@/components/ui/Button';
import { StatusBadge } from '@/components/ui/StatusBadge';
import { StrategyBadge } from '@/components/ui/StrategyBadge';
import { Spinner } from '@/components/ui/Spinner';
import { useOrphanChains, useLinkPosition } from '@/hooks/queries/useChains';
import { formatDate, formatCurrency } from '@/lib/formatters';
import { cn } from '@/lib/utils';

export function OrphanChainsPage() {
  const { data, isLoading, error } = useOrphanChains();
  const linkPosition = useLinkPosition();

  const handleLink = (targetChainId: string, positionId: string) => {
    if (confirm('Link this position to the selected chain?')) {
      linkPosition.mutate({ targetChainId, positionId });
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <p className="text-red-600">Failed to load orphan chains</p>
        </CardContent>
      </Card>
    );
  }

  const orphans = data?.orphans || [];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Link Trades</h1>
          <p className="text-sm text-gray-500 mt-1">
            Single-position trades that may be part of a roll chain
          </p>
        </div>
        <Link to="/chains">
          <Button variant="secondary">Back to Chains</Button>
        </Link>
      </div>

      {orphans.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-gray-500">No orphan trades found</p>
            <p className="text-sm text-gray-400 mt-2">
              All your trades are properly linked to chains
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            Found {orphans.length} single-position trade{orphans.length !== 1 ? 's' : ''} that might be part of a roll chain.
          </p>

          {orphans.map((orphan) => (
            <Card key={orphan.chain.id}>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Link
                      to={`/chains/${orphan.chain.id}`}
                      className="text-lg font-semibold text-gray-900 hover:text-brand-600"
                    >
                      {orphan.chain.underlyingSymbol}
                    </Link>
                    <StrategyBadge strategy={orphan.chain.strategyType as StrategyType} />
                    <StatusBadge status={orphan.chain.status as 'open' | 'closed' | 'partial'} />
                  </div>
                  <span className="text-sm text-gray-500">
                    {formatDate(orphan.chain.openedAt)}
                  </span>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Position summary */}
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">
                      {orphan.position.legs.length} leg{orphan.position.legs.length !== 1 ? 's' : ''}
                    </span>
                    <span className={cn(
                      'font-medium',
                      orphan.position.netPremium >= 0 ? 'text-green-600' : 'text-red-600'
                    )}>
                      {formatCurrency(orphan.position.netPremium)}
                    </span>
                  </div>
                </div>

                {/* Link suggestions */}
                {orphan.suggestions.length > 0 ? (
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-2">
                      Suggested chains to link to:
                    </h4>
                    <div className="space-y-2">
                      {orphan.suggestions.map((suggestion) => (
                        <div
                          key={suggestion.chainId}
                          className={cn(
                            'flex items-center justify-between p-3 rounded-lg border',
                            suggestion.isLikelyRoll
                              ? 'border-green-200 bg-green-50'
                              : 'border-gray-200 bg-white'
                          )}
                        >
                          <div className="flex items-center gap-3">
                            <Link
                              to={`/chains/${suggestion.chainId}`}
                              className="font-medium text-gray-900 hover:text-brand-600"
                            >
                              {suggestion.underlyingSymbol}
                            </Link>
                            <span className="text-xs text-gray-500">
                              {suggestion.positionCount} position{suggestion.positionCount !== 1 ? 's' : ''}
                            </span>
                            <StatusBadge status={suggestion.status as 'open' | 'closed' | 'partial'} size="sm" />
                            {suggestion.isLikelyRoll && (
                              <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
                                Likely roll
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-3">
                            <span className="text-sm text-gray-500">
                              {suggestion.daysDifference} day{suggestion.daysDifference !== 1 ? 's' : ''} apart
                            </span>
                            <Button
                              size="sm"
                              variant={suggestion.isLikelyRoll ? 'primary' : 'secondary'}
                              onClick={() => handleLink(suggestion.chainId, orphan.position.id)}
                              disabled={linkPosition.isPending}
                            >
                              Link
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 italic">
                    No matching chains found within 30 days
                  </p>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
